// VeriLink - Enhanced Rule-based Verification Logic
// Version 4.1 - Trusted-source-aware article scoring (VeriLink v1.0)
// All checks are deterministic, weighted, and reproducible

import { isTrustedSource, parseDomain } from './trustedSources';

// ===============================
// LINK SAFETY SCORE PENALTIES (Start at 100, subtract penalties)
// ===============================
// CRITICAL SIGNALS: Trigger hard cap at 40 max
// NON-CRITICAL: Reduce score normally
const URL_PENALTIES = {
  // CRITICAL SIGNALS (Hard cap trigger)
  DOMAIN_MISMATCH: -60,         // CRITICAL - Brand impersonation
  TYPOSQUATTING: -50,           // CRITICAL - Character substitution attacks
  SUSPICIOUS_CHARACTERS: -50,   // CRITICAL - Unicode/homoglyph attacks
  LOOKALIKE_DOMAIN: -50,        // CRITICAL - Visual impersonation
  KNOWN_SUSPICIOUS: -60,        // CRITICAL - Confirmed malicious
  
  // NON-CRITICAL SIGNALS (Normal deduction)
  SUSPICIOUS_SUBDOMAIN: -30,    // High risk
  IP_ADDRESS: -30,              // High risk
  URL_SHORTENER: -20,           // Moderate risk
  UNCOMMON_TLD: -25,            // Moderate risk (increased from -20)
  MISSING_HTTPS: -15,           // Moderate risk
  MULTIPLE_REDIRECTS: -10,      // Low risk
};

// Critical signal identifiers for hard cap logic
const URL_CRITICAL_SIGNALS = [
  'DOMAIN_MISMATCH',
  'TYPOSQUATTING',
  'SUSPICIOUS_CHARACTERS',
  'LOOKALIKE_DOMAIN',
  'KNOWN_SUSPICIOUS'
];

const URL_BONUSES = {
  VERIFIED_DOMAIN: 10,
  HTTPS_PRESENT: 5,
  CLEAN_SHORT_DOMAIN: 5,
};

// ===============================
// ARTICLE CREDIBILITY PENALTIES (Rebalanced for v1.0 - focus on language not publisher)
// ===============================
const ARTICLE_PENALTIES = {
  ANONYMOUS_SOURCE: 15,         // Reduced (was 35) — publisher reputation handled separately
  EMOTIONAL_MANIPULATION: 30,   // Fear, urgency, panic — language signal (unchanged)
  UNSUPPORTED_STATS: 15,        // Reduced (was 30) — handled via trusted-source bypass
  CONSPIRACY_FRAMING: 40,       // Media hiding truth, suppression (unchanged)
  ABSOLUTE_CLAIMS: 25,          // Never, guaranteed, completely (unchanged)
  SENSATIONALIST_LANGUAGE: 25,  // Shocking, crisis, hidden (unchanged)
  CONTRADICTORY_LOGIC: 20,      // Illogical reasoning (unchanged)
  DANGEROUS_EMBEDDED_URL: 30,   // Flat penalty for URL with Safety <50 (unchanged)
};

// Bonuses (add back, cap at 100)
const ARTICLE_BONUSES = {
  NAMED_INSTITUTIONS: 10,       // Named institutions or authorities
  BALANCED_LANGUAGE: 5,         // Cautious language
  MULTIPLE_SOURCES: 10,         // Multiple referenced sources
  TRUSTED_PUBLISHER: 15,        // Article comes from whitelisted reputable domain
};

// Hard failure signals for combination rules
const HARD_FAILURE_SIGNALS = [
  'ANONYMOUS_SOURCE',
  'EMOTIONAL_MANIPULATION', 
  'UNSUPPORTED_STATS',
  'CONSPIRACY_FRAMING'
];

// ===============================
// EMAIL PHISHING PENALTIES (Boosted Sensitivity with Critical Signals)
// ===============================
// CRITICAL SIGNALS: Trigger hard cap at 40 max safety (60+ risk)
const EMAIL_SENDER_PENALTIES = {
  // CRITICAL SIGNALS (Hard cap trigger)
  DOMAIN_MISMATCH: 60,          // CRITICAL - Brand impersonation
  TYPOSQUATTING: 50,            // CRITICAL - Character substitution
  KNOWN_PHISHING_PATTERN: 50,   // CRITICAL - Known phishing patterns
  
  // NON-CRITICAL SIGNALS (Additive)
  FREE_EMAIL_OFFICIAL: 30,      // High risk
  SUSPICIOUS_SUBDOMAIN: 30,     // High risk
  GENERIC_SENDER: 15,           // Low risk
  UNCOMMON_TLD: 25,             // Moderate risk (increased from 20)
};

// Critical signal identifiers for email
const EMAIL_SENDER_CRITICAL_SIGNALS = [
  'DOMAIN_MISMATCH',
  'TYPOSQUATTING',
  'KNOWN_PHISHING_PATTERN'
];

const EMAIL_BODY_PENALTIES = {
  // CRITICAL SIGNALS (Hard cap trigger)
  CREDENTIAL_REQUEST: 60,       // CRITICAL - Password/login request
  OTP_REQUEST: 60,              // CRITICAL - Verification code request
  DANGEROUS_LINK: 50,           // CRITICAL - Link Safety <50
  
  // NON-CRITICAL SIGNALS (Additive)
  URGENCY_THREAT: 30,           // High risk
  PAYMENT_REQUEST: 40,          // High risk
  EMOTIONAL_MANIPULATION: 25,   // Moderate risk
};

const EMAIL_BODY_CRITICAL_SIGNALS = [
  'CREDENTIAL_REQUEST',
  'OTP_REQUEST',
  'DANGEROUS_LINK'
];

// ===============================
// HARD RED FLAGS DEFINITION
// ===============================
const HARD_RED_FLAGS = {
  URL: URL_CRITICAL_SIGNALS,
  ARTICLE: ['ANONYMOUS_EMOTIONAL_COMBO', 'DANGEROUS_EMBEDDED_URL'],
  EMAIL_SENDER: EMAIL_SENDER_CRITICAL_SIGNALS,
  EMAIL_BODY: EMAIL_BODY_CRITICAL_SIGNALS,
};

// Hard cap thresholds
const HARD_CAP = {
  URL_SAFETY_MAX: 40,           // Max safety score when critical signal detected
  EMAIL_RISK_MIN: 60,           // Min risk score when critical signal detected
};

// ===============================
// SHARED DATA
// ===============================

// Known unsafe domains
const UNSAFE_DOMAINS = [
  'fakebook.com', 'facebok.com', 'faceb00k.com', 'facebook-login.com',
  'g00gle.com', 'gooogle.com', 'googl3.com', 'google-login.com',
  'amaz0n.com', 'amazon-deals.xyz', 'amazonsecure.com',
  'paypa1.com', 'paypal-verify.com', 'paypalsecure.xyz',
  'microsoft-support.xyz', 'apple-id-verify.com',
  'banking-secure.com', 'account-verify.xyz',
  'free-iphone.com', 'winner-prize.com', 'lottery-win.xyz',
  'beforeitsnews.com', 'infowars.com', 'naturalnews.com',
  'worldnewsdailyreport.com', 'empire-news.net',
  'secure-login.com', 'verify-account.com', 'update-billing.com',
];

// URL Shorteners
const URL_SHORTENERS = [
  'bit.ly', 'tinyurl.com', 'goo.gl', 't.co', 'ow.ly', 'is.gd', 
  'buff.ly', 'adf.ly', 'bl.ink', 'short.link', 'cutt.ly', 'rebrand.ly',
  'tiny.cc', 'shorturl.at', 's.id', 'v.gd',
];

// Suspicious TLDs
const SUSPICIOUS_TLDS = [
  '.xyz', '.tk', '.ml', '.ga', '.cf', '.gq', '.top', '.loan', '.work',
  '.click', '.link', '.download', '.stream', '.racing', '.win', '.bid',
  '.party', '.science', '.review', '.country', '.cricket', '.date',
  '.site', '.tech', '.online', '.space', '.fun', '.icu', '.buzz', '.info',
];

// Safe TLDs
const SAFE_TLDS = ['.com', '.org', '.net', '.edu', '.gov', '.mil', '.int', '.io', '.co', '.app', '.dev'];

// Trusted brands
const TRUSTED_BRANDS = [
  'google.com', 'facebook.com', 'amazon.com', 'microsoft.com', 'apple.com',
  'twitter.com', 'linkedin.com', 'netflix.com', 'paypal.com', 'youtube.com',
  'instagram.com', 'github.com', 'stackoverflow.com', 'reddit.com', 'wikipedia.org',
  'bbc.com', 'cnn.com', 'nytimes.com', 'theguardian.com', 'reuters.com',
  // Expanded trustworthy news / press / broadcasters / fact-checkers / agencies
  'apnews.com', 'aljazeera.com', 'channelnewsasia.com', 'cnbc.com', 'nbcnews.com',
  'cbsnews.com', 'abcnews.go.com', 'foxnews.com', 'news.sky.com', 'euronews.com',
  'france24.com', 'dw.com', 'npr.org', 'pbs.org', 'washingtonpost.com',
  'wsj.com', 'ft.com', 'bloomberg.com', 'economist.com', 'time.com',
  'forbes.com', 'politico.com', 'axios.com', 'thehill.com', 'propublica.org',
  'theconversation.com', 'independent.co.uk', 'telegraph.co.uk', 'thetimes.co.uk',
  'irishtimes.com', 'rte.ie', 'cbc.ca', 'ctvnews.ca', 'globalnews.ca',
  'theglobeandmail.com', 'abc.net.au', 'sbs.com.au', 'nzherald.co.nz', 'scmp.com',
  'straitstimes.com', 'japantimes.co.jp', 'thehindu.com', 'ndtv.com', 'indianexpress.com',
  'hindustantimes.com', 'dawn.com', 'arabnews.com', 'haaretz.com', 'timesofisrael.com',
  'lemonde.fr', 'spiegel.de', 'elpais.com', 'snopes.com', 'politifact.com',
  'factcheck.org', 'fullfact.org', 'cnet.com', 'techcrunch.com', 'engadget.com',
  'nature.com', 'science.org', 'scientificamerican.com', 'nationalgeographic.com',
  'who.int', 'cdc.gov', 'nih.gov', 'nasa.gov', 'gov.uk',
  'whitehouse.gov', 'imf.org', 'worldbank.org', 'un.org', 'europa.eu',
];

// Typosquatting patterns
const TYPO_PATTERNS = [
  { brand: 'google', legit: 'google.com', patterns: ['g00gle', 'gogle', 'googel', 'gooogle', 'googl3', 'goolge', 'goggle', 'g0ogle'] },
  { brand: 'facebook', legit: 'facebook.com', patterns: ['facebok', 'faceb00k', 'facebock', 'facbook', 'faecbook', 'facebo0k'] },
  { brand: 'amazon', legit: 'amazon.com', patterns: ['amaz0n', 'amazn', 'amazonn', 'amazom', 'arnazon', 'amzon'] },
  { brand: 'paypal', legit: 'paypal.com', patterns: ['paypa1', 'paypai', 'paypaI', 'paypall', 'paypol', 'paypa|'] },
  { brand: 'apple', legit: 'apple.com', patterns: ['app1e', 'appie', 'aplle', 'aple', 'appIe', 'app|e'] },
  { brand: 'microsoft', legit: 'microsoft.com', patterns: ['micros0ft', 'mircosoft', 'microsft', 'mlcrosoft', 'micr0soft'] },
  { brand: 'netflix', legit: 'netflix.com', patterns: ['netf1ix', 'netfilx', 'neftlix', 'netfIix', 'netfl1x'] },
  { brand: 'instagram', legit: 'instagram.com', patterns: ['lnstagram', 'instagran', 'instagarm', '1nstagram', 'instgram'] },
  { brand: 'twitter', legit: 'twitter.com', patterns: ['twltter', 'twiter', 'twiiter', 'tw1tter', 'twtter'] },
  { brand: 'linkedin', legit: 'linkedin.com', patterns: ['linkedln', 'linkdin', 'linkedim', 'l1nkedin', 'linkedi'] },
  { brand: 'chase', legit: 'chase.com', patterns: ['chas3', 'chace', 'chasse', 'chas'] },
  { brand: 'wellsfargo', legit: 'wellsfargo.com', patterns: ['we11sfargo', 'wellsfarg0', 'weIlsfargo', 'wel1sfargo'] },
  { brand: 'bankofamerica', legit: 'bankofamerica.com', patterns: ['bankofamer1ca', 'bank0famerica', 'bankofamerlca'] },
];

// Lookalike patterns
const LOOKALIKE_PATTERNS = [
  { brand: 'google', patterns: ['goggle', 'gugle', 'gogle', 'googie'] },
  { brand: 'facebook', patterns: ['faceboook', 'facebk', 'faebook', 'fecebook'] },
  { brand: 'amazon', patterns: ['amazone', 'amazzon', 'amaazon', 'amezon'] },
  { brand: 'paypal', patterns: ['paypall', 'paypel', 'paypayl', 'paypai'] },
  { brand: 'microsoft', patterns: ['microsft', 'mircosoft', 'miscrosoft', 'micosoft'] },
  { brand: 'apple', patterns: ['appel', 'aple', 'appple', 'aplle'] },
];

// Brand keywords for mismatch detection
const BRAND_KEYWORDS = [
  { brand: 'google', keywords: ['google', 'gmail', 'goog'] },
  { brand: 'facebook', keywords: ['facebook', 'fb', 'meta'] },
  { brand: 'amazon', keywords: ['amazon', 'aws', 'prime'] },
  { brand: 'paypal', keywords: ['paypal', 'pay-pal'] },
  { brand: 'apple', keywords: ['apple', 'icloud', 'itunes'] },
  { brand: 'microsoft', keywords: ['microsoft', 'outlook', 'office', 'azure', 'hotmail'] },
  { brand: 'netflix', keywords: ['netflix'] },
  { brand: 'bank', keywords: ['bank', 'banking', 'secure-bank', 'chase', 'wellsfargo'] },
];

// ===============================
// TYPOSQUATTING ALGORITHMIC DETECTORS (v1.1)
// Same penalty/cap as before — these only IMPROVE detection coverage.
// ===============================

// Common digit/letter visual substitutions used by attackers
const COMMON_TYPO_SUBSTITUTIONS = {
  '0': 'o',
  '1': 'l',  // also visually equivalent to 'i' — both checked below
  '3': 'e',
  '4': 'a',
  '5': 's',
  '8': 'b',
  '9': 'g',
};

// Brand base names we defend against typosquatting on
const TYPO_TARGET_BRANDS = [
  'google', 'facebook', 'amazon', 'paypal', 'apple', 'microsoft',
  'netflix', 'instagram', 'twitter', 'linkedin', 'youtube', 'github',
  'reddit', 'wikipedia', 'stackoverflow', 'tiktok', 'whatsapp',
  'chase', 'wellsfargo', 'bankofamerica', 'citibank', 'hsbc',
  'bbc', 'cnn', 'nytimes', 'reuters', 'bloomberg',
];

// Skip detection for very short brand collisions to avoid false positives
const MIN_BRAND_LEN_FOR_TYPO = 4;

// Extract DNS labels (subdomains + SLD) from hostname
const getDomainLabels = (host) => (host || '').split('.').filter(l => l.length >= MIN_BRAND_LEN_FOR_TYPO);

/**
 * Character substitution: e.g. g00gle, paypa1, micr0soft, paypa|, app1e, faceb00k.
 * Normalizes attacker-style digit/symbol substitutions back to letters.
 */
export const detectCharacterSubstitution = (host) => {
  const labels = getDomainLabels(host);
  for (const label of labels) {
    let hasSub = false;
    let normalized = '';
    for (const ch of label) {
      if (COMMON_TYPO_SUBSTITUTIONS[ch]) {
        normalized += COMMON_TYPO_SUBSTITUTIONS[ch];
        hasSub = true;
      } else if (ch === '|') {
        normalized += 'l';
        hasSub = true;
      } else {
        normalized += ch;
      }
    }
    if (!hasSub) continue;

    for (const brand of TYPO_TARGET_BRANDS) {
      // Direct match after normalization
      if (normalized === brand && label !== brand) {
        return { matched: true, brand, label, technique: 'substitution', legit: brand + '.com' };
      }
      // Also handle '1' → 'i' (the '1' was already normalized to 'l' above) — re-check with i
      if (normalized.includes('l')) {
        const altI = normalized.replace(/l/g, 'i');
        if (altI === brand && label !== brand) {
          return { matched: true, brand, label, technique: 'substitution', legit: brand + '.com' };
        }
      }
    }
  }
  return null;
};

/**
 * Character omission: e.g. payal (paypal), amazn (amazon), gogle (google).
 * Label length must be brand length - 1.
 */
export const detectCharacterOmission = (host) => {
  const labels = getDomainLabels(host);
  for (const label of labels) {
    for (const brand of TYPO_TARGET_BRANDS) {
      if (label.length !== brand.length - 1) continue;
      if (label === brand) continue;
      for (let i = 0; i < brand.length; i++) {
        const omitted = brand.slice(0, i) + brand.slice(i + 1);
        if (omitted === label) {
          return { matched: true, brand, label, technique: 'omission', legit: brand + '.com' };
        }
      }
    }
  }
  return null;
};

/**
 * Character duplication: e.g. gooogle (google), amazzon (amazon), facebooook (facebook).
 * Label length must be brand length + 1.
 */
export const detectCharacterDuplication = (host) => {
  const labels = getDomainLabels(host);
  for (const label of labels) {
    for (const brand of TYPO_TARGET_BRANDS) {
      if (label.length !== brand.length + 1) continue;
      if (label === brand) continue;
      for (let i = 0; i < brand.length; i++) {
        const dup = brand.slice(0, i + 1) + brand[i] + brand.slice(i + 1);
        if (dup === label) {
          return { matched: true, brand, label, technique: 'duplication', legit: brand + '.com' };
        }
      }
    }
  }
  return null;
};

/**
 * Adjacent character transposition: e.g. goolge (google), mircosoft (microsoft), googel (google).
 * Label length matches brand length.
 */
export const detectCharacterTransposition = (host) => {
  const labels = getDomainLabels(host);
  for (const label of labels) {
    for (const brand of TYPO_TARGET_BRANDS) {
      if (label.length !== brand.length) continue;
      if (label === brand) continue;
      for (let i = 0; i < brand.length - 1; i++) {
        if (brand[i] === brand[i + 1]) continue; // swapping identical chars yields same string
        const arr = brand.split('');
        const tmp = arr[i]; arr[i] = arr[i + 1]; arr[i + 1] = tmp;
        if (arr.join('') === label) {
          return { matched: true, brand, label, technique: 'transposition', legit: brand + '.com' };
        }
      }
    }
  }
  return null;
};

const TECHNIQUE_LABELS = {
  substitution: 'Character Substitution',
  omission: 'Character Omission',
  duplication: 'Character Duplication',
  transposition: 'Character Transposition',
};

/**
 * Runs all four algorithmic detectors. Returns the first positive match (or null).
 * Detection order favors more specific/structural techniques first.
 */
export const detectTyposquatting = (host) => {
  // Skip if the host base is an exact trusted brand (e.g. www.google.com)
  if (!host) return null;
  const labels = (host || '').split('.');
  for (const label of labels) {
    if (TYPO_TARGET_BRANDS.includes(label)) {
      // The hostname literally contains the legitimate brand label — not a typo
      // (We still let outer code check for DOMAIN_MISMATCH separately.)
      return null;
    }
  }
  return (
    detectCharacterSubstitution(host) ||
    detectCharacterTransposition(host) ||
    detectCharacterOmission(host) ||
    detectCharacterDuplication(host)
  );
};

// ===============================
// LINK VERIFICATION (Safety Score)
// ===============================

export const verifyLink = (url) => {
  const result = {
    safetyScore: 100,  // Start at 100 (safe)
    verdict: 'Safe',
    flags: [],
    hardRedFlags: [],
    criticalSignals: [],  // Track which critical signals triggered
    topReasons: [],
    recommendations: [],
    details: {
      hasHttps: false,
      domain: '',
      tld: '',
      isShortener: false,
      isTrustedBrand: false,
      hasTyposquatting: false,
      hasLookalike: false,
      hasDomainMismatch: false,
      hasSuspiciousChars: false,
    }
  };

  try {
    // Clean and parse URL
    let cleanUrl = url.trim();
    if (!cleanUrl.startsWith('http://') && !cleanUrl.startsWith('https://')) {
      cleanUrl = 'https://' + cleanUrl;
    }
    
    const urlObj = new URL(cleanUrl);
    const hostname = urlObj.hostname.toLowerCase();
    const fullUrl = cleanUrl.toLowerCase();
    const pathname = urlObj.pathname;
    
    result.details.domain = hostname;
    result.details.hasHttps = urlObj.protocol === 'https:';
    
    // Extract TLD
    const tldMatch = hostname.match(/\.[a-z]{2,}$/);
    if (tldMatch) {
      result.details.tld = tldMatch[0];
    }

    // ========== CRITICAL SIGNALS (Trigger hard cap at 40) ==========

    // 1. Typosquatting Detection (-50) - CRITICAL
    //    1a) Algorithmic detection (omission/duplication/transposition/substitution)
    const algoTypo = detectTyposquatting(hostname);
    if (algoTypo) {
      result.safetyScore += URL_PENALTIES.TYPOSQUATTING;
      result.details.hasTyposquatting = true;
      result.details.typosquattingTechnique = algoTypo.technique;
      result.criticalSignals.push('TYPOSQUATTING');
      result.hardRedFlags.push('TYPOSQUATTING');
      result.flags.push({
        type: 'danger',
        title: 'Critical Warning: Potential typosquatting detected',
        description: `${TECHNIQUE_LABELS[algoTypo.technique]} — "${algoTypo.label}" appears to impersonate "${algoTypo.brand}". Potential phishing impersonation attempt.`,
        severity: 'critical',
        penalty: URL_PENALTIES.TYPOSQUATTING,
        isHardRedFlag: true,
        isCritical: true,
        technique: algoTypo.technique,
        techniqueLabel: TECHNIQUE_LABELS[algoTypo.technique],
        impersonatedBrand: algoTypo.brand,
      });
    } else {
      //    1b) Fallback: static pattern list (kept for legacy coverage)
      for (const { brand, patterns } of TYPO_PATTERNS) {
        if (patterns.some(p => hostname.includes(p))) {
          result.safetyScore += URL_PENALTIES.TYPOSQUATTING;
          result.details.hasTyposquatting = true;
          result.criticalSignals.push('TYPOSQUATTING');
          result.hardRedFlags.push('TYPOSQUATTING');
          result.flags.push({
            type: 'danger',
            title: 'Critical Warning: Potential typosquatting detected',
            description: `Domain mimics "${brand}" with character substitutions (0→o, 1→l, etc.). Potential phishing impersonation attempt.`,
            severity: 'critical',
            penalty: URL_PENALTIES.TYPOSQUATTING,
            isHardRedFlag: true,
            isCritical: true,
            impersonatedBrand: brand,
          });
          break;
        }
      }
    }

    // 2. Suspicious Characters / Homoglyphs (-50) - CRITICAL
    const homoglyphPattern = /[а-яА-Яàáâãäåæçèéêëìíîïðñòóôõöøùúûüýÿ]/;
    const suspiciousCharPattern = /[|!1l0oO]/;
    const hasHomoglyphs = homoglyphPattern.test(hostname);
    const hasSuspiciousChars = suspiciousCharPattern.test(hostname) && 
      BRAND_KEYWORDS.some(({ keywords }) => keywords.some(kw => hostname.includes(kw.replace(/[0o1li]/gi, ''))));
    
    if (hasHomoglyphs && !result.details.hasTyposquatting) {
      result.safetyScore += URL_PENALTIES.SUSPICIOUS_CHARACTERS;
      result.details.hasSuspiciousChars = true;
      result.criticalSignals.push('SUSPICIOUS_CHARACTERS');
      result.hardRedFlags.push('SUSPICIOUS_CHARACTERS');
      result.flags.push({
        type: 'danger',
        title: 'Suspicious Characters',
        description: 'Domain contains non-standard characters (homoglyphs) used to impersonate legitimate sites',
        severity: 'critical',
        penalty: URL_PENALTIES.SUSPICIOUS_CHARACTERS,
        isHardRedFlag: true,
        isCritical: true
      });
    }

    // 3. Lookalike Domain (-50) - CRITICAL
    for (const { brand, patterns } of LOOKALIKE_PATTERNS) {
      if (patterns.some(p => hostname.includes(p) && !hostname.includes(brand + '.'))) {
        result.safetyScore += URL_PENALTIES.LOOKALIKE_DOMAIN;
        result.details.hasLookalike = true;
        result.criticalSignals.push('LOOKALIKE_DOMAIN');
        result.hardRedFlags.push('LOOKALIKE_DOMAIN');
        result.flags.push({
          type: 'danger',
          title: 'Lookalike Domain',
          description: `Domain appears to impersonate "${brand}" with similar spelling`,
          severity: 'critical',
          penalty: URL_PENALTIES.LOOKALIKE_DOMAIN,
          isHardRedFlag: true,
          isCritical: true
        });
        break;
      }
    }

    // 4. Known Suspicious Domain (-60) - CRITICAL
    const isUnsafe = UNSAFE_DOMAINS.some(domain => hostname.includes(domain));
    if (isUnsafe) {
      result.safetyScore += URL_PENALTIES.KNOWN_SUSPICIOUS;
      result.criticalSignals.push('KNOWN_SUSPICIOUS');
      result.hardRedFlags.push('KNOWN_SUSPICIOUS');
      result.flags.push({
        type: 'danger',
        title: 'Known Malicious Domain',
        description: 'This domain is in our database of confirmed phishing/malware sites',
        severity: 'critical',
        penalty: URL_PENALTIES.KNOWN_SUSPICIOUS,
        isHardRedFlag: true,
        isCritical: true
      });
    }

    // 5. Domain Mismatch (-60) - CRITICAL
    for (const { brand, keywords } of BRAND_KEYWORDS) {
      const hasBrandKeyword = keywords.some(kw => fullUrl.includes(kw));
      const isLegitBrand = TRUSTED_BRANDS.some(tb => hostname === tb || hostname.endsWith('.' + tb));
      
      if (hasBrandKeyword && !isLegitBrand && !result.details.isShortener) {
        const alreadyFlagged = result.flags.some(f => 
          f.title.includes('Typosquatting') || 
          f.title.includes('Lookalike') || 
          f.title.includes('Known Malicious')
        );
        if (!alreadyFlagged) {
          result.safetyScore += URL_PENALTIES.DOMAIN_MISMATCH;
          result.details.hasDomainMismatch = true;
          result.criticalSignals.push('DOMAIN_MISMATCH');
          result.hardRedFlags.push('DOMAIN_MISMATCH');
          result.flags.push({
            type: 'danger',
            title: 'Domain Mismatch',
            description: `URL references "${brand}" but domain doesn't match official website`,
            severity: 'critical',
            penalty: URL_PENALTIES.DOMAIN_MISMATCH,
            isHardRedFlag: true,
            isCritical: true
          });
        }
        break;
      }
    }

    // ========== NON-CRITICAL SIGNALS (Normal deduction) ==========

    // 6. Suspicious Subdomains (-30)
    const subdomainCount = (hostname.match(/\./g) || []).length;
    const hasBrandInSubdomain = BRAND_KEYWORDS.some(({ keywords }) => 
      keywords.some(kw => {
        const parts = hostname.split('.');
        return parts.slice(0, -2).some(part => part.includes(kw));
      })
    );
    
    if (subdomainCount > 2 || hasBrandInSubdomain) {
      result.safetyScore += URL_PENALTIES.SUSPICIOUS_SUBDOMAIN;
      result.flags.push({
        type: 'warning',
        title: 'Suspicious Subdomains',
        description: hasBrandInSubdomain 
          ? 'Brand name in subdomain instead of main domain - common phishing tactic'
          : 'Multiple subdomains used to obscure real domain',
        severity: 'high',
        penalty: URL_PENALTIES.SUSPICIOUS_SUBDOMAIN
      });
    }

    // 7. URL Shortener (-20)
    result.details.isShortener = URL_SHORTENERS.some(s => hostname.includes(s));
    if (result.details.isShortener) {
      result.safetyScore += URL_PENALTIES.URL_SHORTENER;
      result.flags.push({
        type: 'warning',
        title: 'URL Shortener',
        description: 'Shortened URLs mask the true destination - commonly used in phishing',
        severity: 'medium',
        penalty: URL_PENALTIES.URL_SHORTENER
      });
    }

    // 8. Uncommon TLD (-25)
    if (SUSPICIOUS_TLDS.includes(result.details.tld)) {
      result.safetyScore += URL_PENALTIES.UNCOMMON_TLD;
      result.flags.push({
        type: 'warning',
        title: 'Suspicious TLD',
        description: `Uses "${result.details.tld}" - commonly associated with spam/phishing`,
        severity: 'medium',
        penalty: URL_PENALTIES.UNCOMMON_TLD
      });
    }

    // 9. Missing HTTPS (-15)
    if (!result.details.hasHttps) {
      result.safetyScore += URL_PENALTIES.MISSING_HTTPS;
      result.flags.push({
        type: 'warning',
        title: 'No HTTPS',
        description: 'Connection is not encrypted - data could be intercepted',
        severity: 'medium',
        penalty: URL_PENALTIES.MISSING_HTTPS
      });
    }

    // 10. Multiple Redirects (-10)
    const redirectIndicators = pathname.split('/').filter(p => p.length > 0).length;
    const hasRedirectParams = /redirect|goto|url=|link=|out=|redir|forward/i.test(fullUrl);
    if (redirectIndicators > 4 || hasRedirectParams) {
      result.safetyScore += URL_PENALTIES.MULTIPLE_REDIRECTS;
      result.flags.push({
        type: 'info',
        title: 'Redirect Chain',
        description: 'URL structure suggests redirect chains that mask final destination',
        severity: 'low',
        penalty: URL_PENALTIES.MULTIPLE_REDIRECTS
      });
    }

    // 11. IP Address (-30)
    const ipPattern = /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/;
    if (ipPattern.test(hostname)) {
      result.safetyScore += URL_PENALTIES.IP_ADDRESS;
      result.flags.push({
        type: 'danger',
        title: 'IP Address URL',
        description: 'Legitimate websites rarely use direct IP addresses',
        severity: 'high',
        penalty: URL_PENALTIES.IP_ADDRESS
      });
    }

    // ========== BONUSES (Add to score, cap at 100) ==========

    // Only apply bonuses if NO critical signals detected
    if (result.criticalSignals.length === 0) {
      // 12. Verified Domain (+10)
      result.details.isTrustedBrand = TRUSTED_BRANDS.some(tb => hostname === tb || hostname.endsWith('.' + tb));
      if (result.details.isTrustedBrand) {
        result.safetyScore = Math.min(100, result.safetyScore + URL_BONUSES.VERIFIED_DOMAIN);
        result.flags.push({
          type: 'positive',
          title: 'Verified Domain',
          description: 'This is a well-known, trusted brand domain',
          severity: 'positive',
          bonus: URL_BONUSES.VERIFIED_DOMAIN
        });
      }

      // 13. HTTPS Present (+5)
      if (result.details.hasHttps) {
        result.safetyScore = Math.min(100, result.safetyScore + URL_BONUSES.HTTPS_PRESENT);
      }

      // 14. Clean Short Domain (+5)
      const domainParts = hostname.split('.');
      const mainDomain = domainParts.length >= 2 ? domainParts[domainParts.length - 2] : hostname;
      if (mainDomain.length <= 10 && /^[a-z]+$/.test(mainDomain) && SAFE_TLDS.includes(result.details.tld)) {
        result.safetyScore = Math.min(100, result.safetyScore + URL_BONUSES.CLEAN_SHORT_DOMAIN);
        result.flags.push({
          type: 'positive',
          title: 'Clean Domain',
          description: 'Short, clean domain name with standard TLD',
          severity: 'positive',
          bonus: URL_BONUSES.CLEAN_SHORT_DOMAIN
        });
      }
    }

    // ========== HARD CAP: ANY CRITICAL SIGNAL → MAX 40 ==========
    if (result.criticalSignals.length > 0) {
      result.safetyScore = Math.min(result.safetyScore, HARD_CAP.URL_SAFETY_MAX);
    }

    // ========== NORMALIZE SCORE ==========
    result.safetyScore = Math.max(0, Math.min(100, result.safetyScore));

    // ========== DETERMINE VERDICT ==========
    // Sort flags by penalty and get top reasons
    const negativeFlags = result.flags
      .filter(f => f.type !== 'positive')
      .sort((a, b) => Math.abs(b.penalty || 0) - Math.abs(a.penalty || 0));
    result.topReasons = negativeFlags.slice(0, 5);

    // Verdict based on safety score and critical signals
    if (result.criticalSignals.length > 0 || result.safetyScore < 50) {
      result.verdict = 'Dangerous';
      result.recommendations.push('🚨 DANGEROUS: Do NOT click or interact with this link.');
      result.recommendations.push('Report this link to your IT/security team if received.');
      if (result.criticalSignals.length > 0) {
        result.recommendations.push(`⚠️ CRITICAL THREAT: ${result.criticalSignals.join(', ').replace(/_/g, ' ')}`);
      }
    } else if (result.safetyScore < 80) {
      result.verdict = 'Caution';
      result.recommendations.push('⚠️ CAUTION: Several concerns detected. Verify before clicking.');
      result.recommendations.push('Search for the official website directly instead.');
    } else {
      result.verdict = 'Safe';
      result.recommendations.push('✓ This link appears safe based on our analysis.');
      result.recommendations.push('Always remain vigilant when entering sensitive information.');
    }
    
  } catch (error) {
    result.verdict = 'Invalid';
    result.safetyScore = 0;
    result.flags.push({
      type: 'danger',
      title: 'Invalid URL',
      description: 'The provided URL could not be parsed',
      severity: 'critical',
      penalty: -100
    });
    result.topReasons = [result.flags[0]];
    result.recommendations.push('Please enter a valid URL format.');
  }
  
  return result;
};

// ===============================
// ARTICLE VERIFICATION (Stricter)
// ===============================

// Sensationalist Language patterns
const SENSATIONALIST_PATTERNS = [
  'breaking', 'shocking', 'must see', 'incredible', 'unbelievable',
  'explosive', 'bombshell', 'jaw-dropping', 'mind-blowing', 'devastating',
  'terrifying', 'horrifying', 'outrageous', 'scandal', 'chaos',
  'disaster', 'catastrophe', 'nightmare', 'miracle', 'revolutionary',
];

// Absolute Claims patterns
const ABSOLUTE_CLAIMS = [
  'always', 'never', 'completely', 'guaranteed', 'proven fact',
  '100%', 'absolutely', 'definitely', 'certainly', 'undeniably',
  'without doubt', 'no question', 'every single', 'all experts agree',
  'scientifically proven', 'impossible to', 'perfect', 'total',
];

// Clickbait Phrasing
const CLICKBAIT_PHRASES = [
  'you won\'t believe',
  'one weird trick',
  'one simple trick',
  'secret method',
  'doctors hate',
  'this will blow your mind',
  'what happens next',
  'the reason why will shock you',
  'they don\'t want you to know',
  'mainstream media won\'t tell you',
  'must see before deleted',
  'government doesn\'t want',
  'big pharma hates',
  'this changes everything',
  'number 5 will shock you',
  'you\'ll never guess',
  'the truth about',
  'exposed:',
  'revealed:',
];

// Conspiracy Framing patterns (NEW)
const CONSPIRACY_PATTERNS = [
  'they don\'t want you to know',
  'mainstream media won\'t',
  'big pharma',
  'the elites',
  'cover up',
  'coverup',
  'suppressed',
  'censored truth',
  'wake up',
  'sheeple',
  'deep state',
  'hidden agenda',
  'secret agenda',
  'what they\'re hiding',
  'the real truth',
];

// Emotional Manipulation patterns
const EMOTIONAL_MANIPULATION = [
  'act now', 'before it\'s too late', 'urgent', 'don\'t wait',
  'time is running out', 'you\'ll regret', 'lose everything',
  'destroy', 'crush', 'slam', 'blast', 'annihilate',
  'fear', 'panic', 'terrified', 'worried', 'desperate',
  'shocking', 'horrifying', 'devastating',
];

// Expert/Source indicators
const EXPERT_INDICATORS = [
  'dr.', 'professor', 'researcher at', 'scientist at',
  'according to dr', 'university of', 'institute of',
  'peer-reviewed', 'published in', 'journal of',
  'phd', 'md', 'expert in', 'specialist in',
];

const SOURCE_INDICATORS = [
  'according to', 'study shows', 'research indicates',
  'data suggests', 'survey reveals', 'analysis found',
  'report states', 'statistics show', 'source:',
  'reference:', 'citation:', 'cited in',
];

const NEUTRAL_INDICATORS = [
  'furthermore', 'however', 'therefore', 'consequently',
  'in conclusion', 'on the other hand', 'research shows',
  'evidence suggests', 'data indicates', 'it should be noted',
  'in addition', 'moreover', 'nevertheless', 'although',
];

// Extract URLs from text
const extractUrlsFromText = (text) => {
  const urlPattern = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/gi;
  return text.match(urlPattern) || [];
};

export const verifyArticle = (text, sourceUrl = '') => {
  const trusted = isTrustedSource(sourceUrl);
  const sourceDomain = parseDomain(sourceUrl);

  const result = {
    credibilityScore: 100,  // Start at 100, subtract penalties
    category: 'High Credibility',
    flags: [],
    hardFailureSignals: [],  // Track which hard failure signals triggered
    hardRuleTriggered: null,
    hardRuleOverride: false,
    topReasons: [],
    sourceUrl: sourceUrl || '',
    sourceDomain: sourceDomain || '',
    trustedSource: trusted,
    summary: {
      sensationalistCount: 0,
      absoluteClaimCount: 0,
      emotionalCount: 0,
      conspiracyCount: 0,
      sourceCount: 0,
      expertCount: 0,
      neutralCount: 0,
      unsupportedStatsCount: 0,
      dangerousLinkCount: 0,
      embeddedUrlCount: 0,
    },
    recommendations: [],
  };
  
  if (!text || text.trim().length < 50) {
    return {
      ...result,
      credibilityScore: 0,
      category: 'Unknown',
      flags: [{
        type: 'warning',
        title: 'Insufficient Content',
        description: 'Not enough text to analyze (minimum 50 characters)',
        severity: 'medium',
        penalty: 0
      }],
      recommendations: ['Please provide at least 50 characters of text to analyze.']
    };
  }
  
  const lowerText = text.toLowerCase();
  let totalPenalty = 0;
  let totalBonus = 0;

  // ========== PENALTIES (Subtract from 100) ==========

  // 1. Anonymous/Vague Sources (-35)
  let sourceCount = 0;
  SOURCE_INDICATORS.forEach(indicator => {
    if (lowerText.includes(indicator)) sourceCount++;
  });
  result.summary.sourceCount = sourceCount;
  
  // Check for vague source indicators
  const vagueSourcePatterns = [
    'sources say', 'insiders claim', 'unnamed experts', 'anonymous sources',
    'according to sources', 'people familiar with', 'someone close to',
    'a source said', 'sources told', 'industry insiders'
  ];
  const hasVagueSources = vagueSourcePatterns.some(p => lowerText.includes(p));
  
  if (sourceCount === 0 || hasVagueSources) {
    // Trusted publishers act as their own source of record — skip this penalty.
    if (!trusted) {
      totalPenalty += ARTICLE_PENALTIES.ANONYMOUS_SOURCE;
      result.hardFailureSignals.push('ANONYMOUS_SOURCE');
      result.flags.push({
        type: 'danger',
        title: 'Anonymous or Vague Sources',
        description: hasVagueSources 
          ? 'Article relies on unnamed insiders or vague sources without verification'
          : 'Article contains no references to verifiable sources, studies, or experts',
        severity: 'critical',
        penalty: ARTICLE_PENALTIES.ANONYMOUS_SOURCE,
        isHardFailure: true
      });
    }
  }

  // 2. Emotional Manipulation (-30)
  const foundEmotional = [];
  EMOTIONAL_MANIPULATION.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'gi');
    if (regex.test(text)) foundEmotional.push(word);
  });
  result.summary.emotionalCount = foundEmotional.length;
  
  if (foundEmotional.length >= 2) {
    totalPenalty += ARTICLE_PENALTIES.EMOTIONAL_MANIPULATION;
    result.hardFailureSignals.push('EMOTIONAL_MANIPULATION');
    result.flags.push({
      type: 'danger',
      title: 'Emotional Manipulation',
      description: `Found ${foundEmotional.length} fear/urgency/panic triggers designed to bypass rational thinking`,
      severity: 'critical',
      penalty: ARTICLE_PENALTIES.EMOTIONAL_MANIPULATION,
      examples: foundEmotional.slice(0, 4),
      isHardFailure: true
    });
  }

  // 3. Unsupported Statistics (-30)
  const statPattern = /\d+(\.\d+)?%|\d+ (out of|in|percent|million|billion)/gi;
  const statsFound = text.match(statPattern) || [];
  result.summary.unsupportedStatsCount = statsFound.length;
  
  if (statsFound.length > 0 && sourceCount === 0) {
    // Trusted publishers are presumed to back their own statistics — skip this penalty.
    if (!trusted) {
      totalPenalty += ARTICLE_PENALTIES.UNSUPPORTED_STATS;
      result.hardFailureSignals.push('UNSUPPORTED_STATS');
      result.flags.push({
        type: 'danger',
        title: 'Unsupported Statistics',
        description: `Contains ${statsFound.length} statistics/percentages without any cited sources`,
        severity: 'critical',
        penalty: ARTICLE_PENALTIES.UNSUPPORTED_STATS,
        examples: statsFound.slice(0, 3),
        isHardFailure: true
      });
    }
  }

  // 4. Conspiracy/Suppression Framing (-40)
  const foundConspiracy = [];
  CONSPIRACY_PATTERNS.forEach(pattern => {
    if (lowerText.includes(pattern)) foundConspiracy.push(pattern);
  });
  result.summary.conspiracyCount = foundConspiracy.length;
  
  if (foundConspiracy.length > 0) {
    totalPenalty += ARTICLE_PENALTIES.CONSPIRACY_FRAMING;
    result.hardFailureSignals.push('CONSPIRACY_FRAMING');
    result.flags.push({
      type: 'danger',
      title: 'Conspiracy/Suppression Framing',
      description: `Found ${foundConspiracy.length} claims about media/institutions hiding truth`,
      severity: 'critical',
      penalty: ARTICLE_PENALTIES.CONSPIRACY_FRAMING,
      examples: foundConspiracy.slice(0, 3),
      isHardFailure: true
    });
  }

  // 5. Absolute Claims (-25)
  const foundAbsolute = [];
  ABSOLUTE_CLAIMS.forEach(claim => {
    if (lowerText.includes(claim)) foundAbsolute.push(claim);
  });
  result.summary.absoluteClaimCount = foundAbsolute.length;
  
  if (foundAbsolute.length > 0) {
    totalPenalty += ARTICLE_PENALTIES.ABSOLUTE_CLAIMS;
    result.flags.push({
      type: 'warning',
      title: 'Absolute Claims',
      description: `Found ${foundAbsolute.length} absolute statements ("never", "guaranteed", "completely")`,
      severity: 'high',
      penalty: ARTICLE_PENALTIES.ABSOLUTE_CLAIMS,
      examples: foundAbsolute.slice(0, 4)
    });
  }

  // 6. Sensationalist Language (-25)
  const foundSensationalist = [];
  SENSATIONALIST_PATTERNS.forEach(pattern => {
    if (lowerText.includes(pattern)) foundSensationalist.push(pattern);
  });
  result.summary.sensationalistCount = foundSensationalist.length;
  
  if (foundSensationalist.length > 0) {
    totalPenalty += ARTICLE_PENALTIES.SENSATIONALIST_LANGUAGE;
    result.flags.push({
      type: 'warning',
      title: 'Sensationalist Language',
      description: `Found ${foundSensationalist.length} sensationalist terms (shocking, crisis, hidden)`,
      severity: 'high',
      penalty: ARTICLE_PENALTIES.SENSATIONALIST_LANGUAGE,
      examples: foundSensationalist.slice(0, 4)
    });
  }

  // 7. Contradictory/Illogical Reasoning (-20)
  const words = text.split(/\s+/);
  const capsWords = words.filter(w => w.length > 3 && w === w.toUpperCase() && /[A-Z]/.test(w));
  const exclamations = (text.match(/!/g) || []).length;
  
  if (capsWords.length > 5 || exclamations > 5) {
    totalPenalty += ARTICLE_PENALTIES.CONTRADICTORY_LOGIC;
    result.flags.push({
      type: 'warning',
      title: 'Poor Writing Quality / Illogical',
      description: `Excessive ALL-CAPS (${capsWords.length}) and exclamation points (${exclamations}) indicate low quality`,
      severity: 'medium',
      penalty: ARTICLE_PENALTIES.CONTRADICTORY_LOGIC
    });
  }

  // 8. Dangerous Embedded URLs (-30 flat if URL Safety <50)
  // Only apply if URLs are present in the article
  const embeddedUrls = extractUrlsFromText(text);
  result.summary.embeddedUrlCount = embeddedUrls.length;
  let dangerousLinkCount = 0;
  
  if (embeddedUrls.length > 0) {
    embeddedUrls.forEach(url => {
      const urlResult = verifyLink(url);
      if (urlResult.safetyScore < 50) {
        dangerousLinkCount++;
      }
    });
    result.summary.dangerousLinkCount = dangerousLinkCount;
    
    if (dangerousLinkCount > 0) {
      totalPenalty += ARTICLE_PENALTIES.DANGEROUS_EMBEDDED_URL;
      result.flags.push({
        type: 'danger',
        title: 'Dangerous Embedded Links',
        description: `Contains ${dangerousLinkCount} link(s) with Safety Score < 50`,
        severity: 'critical',
        penalty: ARTICLE_PENALTIES.DANGEROUS_EMBEDDED_URL
      });
    }
  }

  // ========== BONUSES (Add back, cap at 100) ==========

  // 8.5 Trusted Publisher (+15) — domain on curated whitelist
  if (trusted) {
    totalBonus += ARTICLE_BONUSES.TRUSTED_PUBLISHER;
    result.flags.push({
      type: 'positive',
      title: 'Trusted Publisher',
      description: `Published by ${sourceDomain} — an established reputable source`,
      severity: 'positive',
      bonus: ARTICLE_BONUSES.TRUSTED_PUBLISHER
    });
  }

  // 9. Named Institutions/Authorities (+10)
  let expertCount = 0;
  EXPERT_INDICATORS.forEach(indicator => {
    if (lowerText.includes(indicator)) expertCount++;
  });
  result.summary.expertCount = expertCount;
  
  if (expertCount >= 2) {
    totalBonus += ARTICLE_BONUSES.NAMED_INSTITUTIONS;
    result.flags.push({
      type: 'positive',
      title: 'Named Institutions/Authorities',
      description: `References ${expertCount} experts, researchers, or institutions by name`,
      severity: 'positive',
      bonus: ARTICLE_BONUSES.NAMED_INSTITUTIONS
    });
  }

  // 10. Balanced/Cautious Language (+5)
  let neutralCount = 0;
  NEUTRAL_INDICATORS.forEach(indicator => {
    if (lowerText.includes(indicator)) neutralCount++;
  });
  result.summary.neutralCount = neutralCount;
  
  if (neutralCount >= 3) {
    totalBonus += ARTICLE_BONUSES.BALANCED_LANGUAGE;
    result.flags.push({
      type: 'positive',
      title: 'Balanced, Cautious Language',
      description: 'Uses balanced, analytical language with logical transitions',
      severity: 'positive',
      bonus: ARTICLE_BONUSES.BALANCED_LANGUAGE
    });
  }

  // 11. Multiple Referenced Sources (+10)
  if (sourceCount >= 3) {
    totalBonus += ARTICLE_BONUSES.MULTIPLE_SOURCES;
    result.flags.push({
      type: 'positive',
      title: 'Multiple Referenced Sources',
      description: `Contains ${sourceCount} references to sources or citations`,
      severity: 'positive',
      bonus: ARTICLE_BONUSES.MULTIPLE_SOURCES
    });
  }

  // ========== CALCULATE BASE SCORE ==========
  let calculatedScore = 100 - totalPenalty + totalBonus;
  calculatedScore = Math.max(0, Math.min(100, calculatedScore));

  // ========== HARD FAILURE RULES (Override numerical score) ==========
  const hardFailureCount = result.hardFailureSignals.length;
  
  // If 3+ hard failure signals → immediately LOW CREDIBILITY, score ≤25
  if (hardFailureCount >= 3) {
    result.hardRuleOverride = true;
    result.hardRuleTriggered = `Three or more critical signals detected (${result.hardFailureSignals.join(', ')}). Article classified as LOW CREDIBILITY.`;
    result.credibilityScore = Math.min(calculatedScore, 25);
  }
  // If any 2 hard failure signals → cap at ≤40
  else if (hardFailureCount >= 2) {
    result.hardRuleOverride = true;
    result.hardRuleTriggered = `Two critical signals detected (${result.hardFailureSignals.join(', ')}). Credibility capped at 40.`;
    result.credibilityScore = Math.min(calculatedScore, 40);
  }
  // No hard rule override
  else {
    result.credibilityScore = calculatedScore;
  }

  // ========== DETERMINE CATEGORY ==========
  if (result.credibilityScore >= 80) {
    result.category = 'High Credibility';
  } else if (result.credibilityScore >= 50) {
    result.category = 'Mixed / Uncertain';
  } else {
    result.category = 'Low Credibility';
  }

  // Get top 5 reasons (negative flags sorted by penalty)
  const negativeFlags = result.flags
    .filter(f => f.type !== 'positive')
    .sort((a, b) => (b.penalty || 0) - (a.penalty || 0));
  result.topReasons = negativeFlags.slice(0, 5);

  // ========== RECOMMENDATIONS ==========
  if (result.category === 'Low Credibility') {
    result.recommendations.push('🚨 LOW CREDIBILITY: Multiple critical issues detected.');
    if (result.hardRuleTriggered) {
      result.recommendations.push(`⚠️ HARD RULE OVERRIDE: ${result.hardRuleTriggered}`);
    }
    result.recommendations.push('Do NOT share. Verify with established, reputable sources.');
  } else if (result.category === 'Mixed / Uncertain') {
    result.recommendations.push('⚠️ MIXED CREDIBILITY: Some concerns detected.');
    result.recommendations.push('Cross-reference claims with trusted sources before sharing.');
  } else {
    result.recommendations.push('✓ Article appears credible based on our analysis.');
    result.recommendations.push('Always verify important claims independently.');
  }
  
  return result;
};

// ===============================
// EMAIL PHISHING VERIFICATION (Boosted Sensitivity)
// ===============================

// Legitimate organization domains
const LEGITIMATE_ORG_DOMAINS = {
  'google': ['google.com', 'gmail.com', 'googlemail.com'],
  'microsoft': ['microsoft.com', 'outlook.com', 'hotmail.com', 'live.com', 'office.com', 'office365.com'],
  'apple': ['apple.com', 'icloud.com', 'me.com'],
  'amazon': ['amazon.com', 'amazon.co.uk', 'amazon.de', 'amazonses.com'],
  'facebook': ['facebook.com', 'fb.com', 'meta.com'],
  'paypal': ['paypal.com', 'paypal.me'],
  'netflix': ['netflix.com'],
  'bank of america': ['bankofamerica.com', 'bofa.com'],
  'chase': ['chase.com', 'jpmchase.com'],
  'wells fargo': ['wellsfargo.com'],
  'linkedin': ['linkedin.com'],
  'twitter': ['twitter.com', 'x.com'],
};

// Free email providers
const FREE_EMAIL_PROVIDERS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'live.com', 'msn.com', 'me.com', 'inbox.com',
];

// Generic sender prefixes
const GENERIC_SENDER_PREFIXES = [
  'support', 'admin', 'security', 'account', 'billing', 'noreply', 'no-reply',
  'helpdesk', 'service', 'alert', 'notification', 'verify', 'update',
  'confirm', 'info', 'contact', 'team', 'customerservice', 'help',
];

// Email typo patterns
const EMAIL_TYPO_PATTERNS = [
  { brand: 'google', patterns: ['g00gle', 'gogle', 'googel', 'gooogle', 'googl3', 'goolge', 'goggle'] },
  { brand: 'gmail', patterns: ['gmai1', 'gmall', 'gmal', 'gmaiI', 'gmaill', 'gnail'] },
  { brand: 'microsoft', patterns: ['micros0ft', 'mircosoft', 'microsft', 'mlcrosoft', 'micosoft'] },
  { brand: 'outlook', patterns: ['outl00k', 'outlok', 'outlock', '0utlook'] },
  { brand: 'paypal', patterns: ['paypa1', 'paypai', 'paypaI', 'paypall', 'paypol'] },
  { brand: 'apple', patterns: ['app1e', 'appie', 'aplle', 'aple', 'appIe'] },
  { brand: 'amazon', patterns: ['amaz0n', 'amazn', 'amazonn', 'amazom', 'arnazon'] },
  { brand: 'netflix', patterns: ['netf1ix', 'netfilx', 'neftlix', 'netfIix'] },
  { brand: 'facebook', patterns: ['facebok', 'faceb00k', 'facebock', 'facbook', 'faecbook'] },
  { brand: 'chase', patterns: ['chas3', 'chace', 'chasse'] },
  { brand: 'wellsfargo', patterns: ['we11sfargo', 'wellsfarg0', 'weIlsfargo'] },
];

// Urgency/threat language
const URGENCY_PHRASES = [
  'urgent action required', 'immediate action', 'act now', 'expires today',
  'last chance', 'final warning', 'account suspended', 'account will be closed',
  'unauthorized activity', 'security alert', 'verify immediately',
  'confirm within 24 hours', 'limited time', 'time sensitive',
  'your account has been compromised', 'unusual sign-in', 'suspicious activity',
  'failure to respond', 'legal action', 'account terminated',
];

// Credential/Sensitive requests - HARD RED FLAG
const CREDENTIAL_REQUESTS = [
  'password', 'passcode', 'login credentials', 'sign-in details',
  'enter your password', 'confirm your password', 'verify your password',
  'update your password', 'reset your password',
];

const OTP_REQUESTS = [
  'one-time password', 'otp', 'verification code', 'security code',
  'authentication code', 'confirm code', '6-digit code', '4-digit code',
  'enter the code', 'sms code', 'text code',
];

const PAYMENT_REQUESTS = [
  'credit card', 'card number', 'cvv', 'cvc', 'expiry date',
  'bank account', 'routing number', 'social security', 'ssn',
  'payment details', 'billing information', 'update payment',
];

// Emotional manipulation in emails
const EMAIL_EMOTIONAL_MANIPULATION = [
  'congratulations', 'you\'ve won', 'winner', 'prize', 'reward',
  'fear', 'worried', 'concerned', 'urgent', 'emergency',
  'don\'t miss', 'once in a lifetime', 'exclusive offer',
  'limited time', 'act fast', 'immediate', 'critical',
];

// Extract links from content
const extractLinksFromContent = (content) => {
  const urlPattern = /https?:\/\/[^\s<>"{}|\\^`\[\]]+/gi;
  return content.match(urlPattern) || [];
};

// Verify email sender
export const verifyEmailSender = (email) => {
  const result = {
    riskScore: 0,
    flags: [],
    hardRedFlags: [],
    criticalSignals: [],  // Track critical signals
    details: {
      localPart: '',
      domain: '',
      tld: '',
      isFreeProvider: false,
      hasTypo: false,
      hasSuspiciousPrefix: false,
      hasDomainMismatch: false,
    },
  };

  if (!email || !email.includes('@')) {
    result.riskScore = 100;
    result.flags.push({
      type: 'danger',
      title: 'Invalid Email',
      description: 'Email address format is invalid',
      severity: 'critical',
      penalty: 100
    });
    return result;
  }

  const parts = email.toLowerCase().trim().split('@');
  const localPart = parts[0];
  const domain = parts[1];
  
  result.details.localPart = localPart;
  result.details.domain = domain;

  // Extract TLD
  const tldMatch = domain.match(/\.[a-z]{2,}$/);
  if (tldMatch) {
    result.details.tld = tldMatch[0];
  }

  // ========== CRITICAL SIGNALS (Trigger hard cap) ==========

  // 1. Domain Mismatch (+60) - CRITICAL
  for (const [org, domains] of Object.entries(LEGITIMATE_ORG_DOMAINS)) {
    const orgPattern = org.replace(/\s+/g, '').toLowerCase();
    if (localPart.includes(orgPattern) || domain.includes(orgPattern.substring(0, 4))) {
      if (!domains.some(d => domain === d || domain.endsWith('.' + d))) {
        result.riskScore += EMAIL_SENDER_PENALTIES.DOMAIN_MISMATCH;
        result.details.hasDomainMismatch = true;
        result.criticalSignals.push('DOMAIN_MISMATCH');
        result.hardRedFlags.push('DOMAIN_MISMATCH');
        result.flags.push({
          type: 'danger',
          title: 'Domain Mismatch',
          description: `Claims to be from "${org}" but domain doesn't match official addresses`,
          severity: 'critical',
          penalty: EMAIL_SENDER_PENALTIES.DOMAIN_MISMATCH,
          isHardRedFlag: true,
          isCritical: true
        });
        break;
      }
    }
  }

  // 2. Typosquatting (+50) - CRITICAL
  for (const { brand, patterns } of EMAIL_TYPO_PATTERNS) {
    if (patterns.some(p => domain.includes(p))) {
      result.riskScore += EMAIL_SENDER_PENALTIES.TYPOSQUATTING;
      result.details.hasTypo = true;
      result.criticalSignals.push('TYPOSQUATTING');
      result.hardRedFlags.push('TYPOSQUATTING');
      result.flags.push({
        type: 'danger',
        title: 'Typosquatting',
        description: `Domain mimics "${brand}" with character substitutions`,
        severity: 'critical',
        penalty: EMAIL_SENDER_PENALTIES.TYPOSQUATTING,
        isHardRedFlag: true,
        isCritical: true
      });
      break;
    }
  }

  // 3. Known Phishing Pattern (+50) - CRITICAL
  const knownPhishingPatterns = [
    'secure-', '-secure', 'verify-', '-verify', 'account-', '-account',
    'login-', '-login', 'update-', '-update', 'confirm-', '-confirm',
    'support-team', 'helpdesk-', 'security-alert', 'billing-'
  ];
  const hasPhishingPattern = knownPhishingPatterns.some(p => domain.includes(p));
  if (hasPhishingPattern && !result.details.hasDomainMismatch && !result.details.hasTypo) {
    result.riskScore += EMAIL_SENDER_PENALTIES.KNOWN_PHISHING_PATTERN;
    result.criticalSignals.push('KNOWN_PHISHING_PATTERN');
    result.hardRedFlags.push('KNOWN_PHISHING_PATTERN');
    result.flags.push({
      type: 'danger',
      title: 'Known Phishing Pattern',
      description: 'Domain structure matches common phishing patterns',
      severity: 'critical',
      penalty: EMAIL_SENDER_PENALTIES.KNOWN_PHISHING_PATTERN,
      isHardRedFlag: true,
      isCritical: true
    });
  }

  // ========== NON-CRITICAL SIGNALS (Additive) ==========

  // 4. Free Email Posing as Official (+30)
  result.details.isFreeProvider = FREE_EMAIL_PROVIDERS.some(provider => domain === provider);
  const hasSuspiciousPrefix = GENERIC_SENDER_PREFIXES.some(prefix => 
    localPart === prefix || localPart.startsWith(prefix + '.') || localPart.startsWith(prefix + '-')
  );
  result.details.hasSuspiciousPrefix = hasSuspiciousPrefix;
  
  if (hasSuspiciousPrefix && result.details.isFreeProvider) {
    result.riskScore += EMAIL_SENDER_PENALTIES.FREE_EMAIL_OFFICIAL;
    result.flags.push({
      type: 'warning',
      title: 'Suspicious Free Email',
      description: `Official-sounding name "${localPart}@" from free email provider`,
      severity: 'high',
      penalty: EMAIL_SENDER_PENALTIES.FREE_EMAIL_OFFICIAL
    });
  }

  // 5. Suspicious Subdomains (+30)
  const subdomainCount = (domain.match(/\./g) || []).length;
  if (subdomainCount > 2) {
    result.riskScore += EMAIL_SENDER_PENALTIES.SUSPICIOUS_SUBDOMAIN;
    result.flags.push({
      type: 'warning',
      title: 'Suspicious Subdomains',
      description: 'Multiple subdomains used to disguise sender domain',
      severity: 'high',
      penalty: EMAIL_SENDER_PENALTIES.SUSPICIOUS_SUBDOMAIN
    });
  }

  // 6. Generic Sender (+15)
  if (hasSuspiciousPrefix && !result.details.isFreeProvider) {
    result.riskScore += EMAIL_SENDER_PENALTIES.GENERIC_SENDER;
    result.flags.push({
      type: 'info',
      title: 'Generic Sender',
      description: `Generic sender name "${localPart}@" - common in phishing`,
      severity: 'low',
      penalty: EMAIL_SENDER_PENALTIES.GENERIC_SENDER
    });
  }

  // 7. Uncommon TLD (+25)
  if (SUSPICIOUS_TLDS.includes(result.details.tld)) {
    result.riskScore += EMAIL_SENDER_PENALTIES.UNCOMMON_TLD;
    result.flags.push({
      type: 'warning',
      title: 'Suspicious TLD',
      description: `Uses "${result.details.tld}" - common in spam/phishing`,
      severity: 'medium',
      penalty: EMAIL_SENDER_PENALTIES.UNCOMMON_TLD
    });
  }

  // ========== HARD CAP: ANY CRITICAL SIGNAL → MIN 60 RISK ==========
  if (result.criticalSignals.length > 0) {
    result.riskScore = Math.max(result.riskScore, HARD_CAP.EMAIL_RISK_MIN);
  }

  // Normalize
  result.riskScore = Math.min(100, result.riskScore);

  return result;
};

// Verify email content
export const verifyEmailContent = (content) => {
  const result = {
    riskScore: 0,
    flags: [],
    hardRedFlags: [],
    criticalSignals: [],  // Track critical signals
    hardRuleTriggered: null,
    summary: {
      urgencyPhraseCount: 0,
      credentialRequestCount: 0,
      otpRequestCount: 0,
      paymentRequestCount: 0,
      dangerousLinkCount: 0,
      emotionalCount: 0,
      extractedLinks: [],
    },
  };

  if (!content || content.trim().length < 20) {
    return result;
  }

  const lowerContent = content.toLowerCase();

  // ========== CRITICAL SIGNALS (Trigger hard cap) ==========

  // 1. Credential Requests (+60) - CRITICAL - IMMEDIATE HIGH RISK
  const foundCredentials = [];
  CREDENTIAL_REQUESTS.forEach(term => {
    if (lowerContent.includes(term)) foundCredentials.push(term);
  });
  result.summary.credentialRequestCount = foundCredentials.length;
  
  if (foundCredentials.length > 0) {
    result.riskScore += EMAIL_BODY_PENALTIES.CREDENTIAL_REQUEST;
    result.criticalSignals.push('CREDENTIAL_REQUEST');
    result.hardRedFlags.push('CREDENTIAL_REQUEST');
    result.hardRuleTriggered = 'Email requests password/login credentials - classic phishing';
    result.flags.push({
      type: 'danger',
      title: 'Requests Credentials',
      description: `Email requests login credentials - NEVER share passwords via email`,
      severity: 'critical',
      penalty: EMAIL_BODY_PENALTIES.CREDENTIAL_REQUEST,
      isHardRedFlag: true,
      isCritical: true,
      examples: foundCredentials.slice(0, 3)
    });
  }

  // 2. OTP Requests (+60) - CRITICAL - IMMEDIATE HIGH RISK
  const foundOtp = [];
  OTP_REQUESTS.forEach(term => {
    if (lowerContent.includes(term)) foundOtp.push(term);
  });
  result.summary.otpRequestCount = foundOtp.length;
  
  if (foundOtp.length > 0) {
    result.riskScore += EMAIL_BODY_PENALTIES.OTP_REQUEST;
    result.criticalSignals.push('OTP_REQUEST');
    result.hardRedFlags.push('OTP_REQUEST');
    result.hardRuleTriggered = 'Email requests OTP/verification code - classic phishing';
    result.flags.push({
      type: 'danger',
      title: 'Requests OTP/Code',
      description: `Email requests verification codes - NEVER share OTPs via email`,
      severity: 'critical',
      penalty: EMAIL_BODY_PENALTIES.OTP_REQUEST,
      isHardRedFlag: true,
      isCritical: true,
      examples: foundOtp.slice(0, 3)
    });
  }

  // 3. Dangerous Embedded Links (+50) - CRITICAL
  const extractedLinks = extractLinksFromContent(content);
  result.summary.extractedLinks = extractedLinks;
  
  let dangerousLinkCount = 0;
  extractedLinks.forEach(link => {
    const linkResult = verifyLink(link);
    if (linkResult.safetyScore < 50) {
      dangerousLinkCount++;
    }
  });
  result.summary.dangerousLinkCount = dangerousLinkCount;
  
  if (dangerousLinkCount > 0) {
    result.riskScore += EMAIL_BODY_PENALTIES.DANGEROUS_LINK;
    result.criticalSignals.push('DANGEROUS_LINK');
    result.hardRedFlags.push('DANGEROUS_LINK');
    result.flags.push({
      type: 'danger',
      title: 'Dangerous Links',
      description: `Contains ${dangerousLinkCount} link(s) with Safety Score < 50`,
      severity: 'critical',
      penalty: EMAIL_BODY_PENALTIES.DANGEROUS_LINK,
      isHardRedFlag: true,
      isCritical: true
    });
  }

  // ========== NON-CRITICAL SIGNALS (Additive) ==========

  // 4. Payment Info Requests (+40)
  const foundPayment = [];
  PAYMENT_REQUESTS.forEach(term => {
    if (lowerContent.includes(term)) foundPayment.push(term);
  });
  result.summary.paymentRequestCount = foundPayment.length;
  
  if (foundPayment.length > 0) {
    const penalty = Math.min(foundPayment.length * 20, EMAIL_BODY_PENALTIES.PAYMENT_REQUEST);
    result.riskScore += penalty;
    result.flags.push({
      type: 'danger',
      title: 'Requests Payment Info',
      description: `Email requests sensitive financial information`,
      severity: 'critical',
      penalty,
      examples: foundPayment.slice(0, 3)
    });
  }

  // 5. Urgency/Threat Language (+30)
  const foundUrgency = [];
  URGENCY_PHRASES.forEach(phrase => {
    if (lowerContent.includes(phrase)) foundUrgency.push(phrase);
  });
  result.summary.urgencyPhraseCount = foundUrgency.length;
  
  if (foundUrgency.length > 0) {
    const penalty = Math.min(foundUrgency.length * 10, EMAIL_BODY_PENALTIES.URGENCY_THREAT);
    result.riskScore += penalty;
    result.flags.push({
      type: 'warning',
      title: 'Urgency/Threats',
      description: `Found ${foundUrgency.length} pressure tactics to force quick action`,
      severity: 'high',
      penalty,
      examples: foundUrgency.slice(0, 3)
    });
  }

  // 6. Emotional Manipulation (+25)
  const foundEmotional = [];
  EMAIL_EMOTIONAL_MANIPULATION.forEach(word => {
    if (lowerContent.includes(word)) foundEmotional.push(word);
  });
  result.summary.emotionalCount = foundEmotional.length;
  
  if (foundEmotional.length >= 2) {
    const penalty = Math.min(foundEmotional.length * 8, EMAIL_BODY_PENALTIES.EMOTIONAL_MANIPULATION);
    result.riskScore += penalty;
    result.flags.push({
      type: 'warning',
      title: 'Emotional Manipulation',
      description: `Found ${foundEmotional.length} emotional trigger words`,
      severity: 'medium',
      penalty,
      examples: foundEmotional.slice(0, 3)
    });
  }

  // ========== HARD CAP: ANY CRITICAL SIGNAL → MIN 60 RISK ==========
  if (result.criticalSignals.length > 0) {
    result.riskScore = Math.max(result.riskScore, HARD_CAP.EMAIL_RISK_MIN);
  }

  // Normalize
  result.riskScore = Math.min(100, result.riskScore);

  return result;
};

// Combined email verification
export const verifyEmail = (emailAddress, emailContent = '') => {
  const result = {
    overallRiskScore: 0,
    verdict: 'Unknown',
    senderAnalysis: null,
    contentAnalysis: null,
    flags: [],
    hardRedFlags: [],
    criticalSignals: [],  // Track all critical signals
    hardRuleTriggered: null,
    topReasons: [],
    recommendations: [],
  };

  // Analyze sender
  const senderResult = verifyEmailSender(emailAddress);
  result.senderAnalysis = senderResult;
  result.hardRedFlags = [...senderResult.hardRedFlags];
  result.criticalSignals = [...(senderResult.criticalSignals || [])];

  // Analyze content if provided
  if (emailContent && emailContent.trim().length >= 20) {
    result.contentAnalysis = verifyEmailContent(emailContent);
    result.hardRedFlags = [...result.hardRedFlags, ...result.contentAnalysis.hardRedFlags];
    result.criticalSignals = [...result.criticalSignals, ...(result.contentAnalysis.criticalSignals || [])];
    if (result.contentAnalysis.hardRuleTriggered) {
      result.hardRuleTriggered = result.contentAnalysis.hardRuleTriggered;
    }
  }

  // Calculate combined score (weighted)
  if (result.contentAnalysis) {
    result.overallRiskScore = Math.round(
      (senderResult.riskScore * 0.4) + 
      (result.contentAnalysis.riskScore * 0.6)
    );
  } else {
    result.overallRiskScore = senderResult.riskScore;
  }

  // ========== HARD CAP: ANY CRITICAL SIGNAL → MIN 60 RISK ==========
  if (result.criticalSignals.length > 0) {
    result.overallRiskScore = Math.max(result.overallRiskScore, HARD_CAP.EMAIL_RISK_MIN);
  }

  // Normalize
  result.overallRiskScore = Math.min(100, result.overallRiskScore);

  // Collect all flags
  result.flags = [...senderResult.flags];
  if (result.contentAnalysis) {
    result.flags = [...result.flags, ...result.contentAnalysis.flags];
  }

  // Sort by penalty and get top 5
  const negativeFlags = [...result.flags]
    .filter(f => f.type !== 'positive')
    .sort((a, b) => (b.penalty || 0) - (a.penalty || 0));
  result.topReasons = negativeFlags.slice(0, 5);

  // ========== DETERMINE VERDICT ==========
  // CRITICAL: Any critical signal = HIGH RISK immediately
  const hasCriticalSignal = result.criticalSignals.length > 0;
  
  if (hasCriticalSignal || result.overallRiskScore >= 60) {
    result.verdict = 'High Phishing Risk';
    result.recommendations.push('🚨 HIGH RISK: Do NOT click links or provide ANY information.');
    if (result.hardRuleTriggered) {
      result.recommendations.push(`⚠️ CRITICAL: ${result.hardRuleTriggered}`);
    }
    if (result.criticalSignals.length > 0) {
      result.recommendations.push(`⚠️ CRITICAL SIGNALS: ${result.criticalSignals.join(', ').replace(/_/g, ' ')}`);
    }
    result.recommendations.push('Report this email as phishing immediately.');
    result.recommendations.push('If you clicked any links, change your passwords NOW.');
  } else if (result.overallRiskScore >= 30) {
    result.verdict = 'Suspicious';
    result.recommendations.push('⚠️ SUSPICIOUS: Several red flags detected.');
    result.recommendations.push('Contact the organization directly through official channels.');
    result.recommendations.push('Do not click links or reply to this email.');
  } else {
    result.verdict = 'Likely Legitimate';
    result.recommendations.push('✓ Email appears legitimate based on our analysis.');
    result.recommendations.push('Always verify sender for sensitive requests.');
  }

  return result;
};

// ===============================
// GAMIFICATION LOGIC
// ===============================

export const BADGES = [
  { id: 'first_check', name: 'First Step', description: 'Completed your first verification', icon: 'award', threshold: 1 },
  { id: 'careful_10', name: 'Careful Browser', description: 'Verified 10 links, articles, or emails', icon: 'shield', threshold: 10 },
  { id: 'vigilant_25', name: 'Vigilant User', description: 'Verified 25 items', icon: 'eye', threshold: 25 },
  { id: 'expert_50', name: 'Verification Expert', description: 'Verified 50 items', icon: 'star', threshold: 50 },
  { id: 'master_100', name: 'Truth Seeker', description: 'Verified 100 items', icon: 'crown', threshold: 100 },
  { id: 'streak_3', name: 'On a Roll', description: '3-day verification streak', icon: 'flame', threshold: 3, type: 'streak' },
  { id: 'streak_7', name: 'Week Warrior', description: '7-day verification streak', icon: 'zap', threshold: 7, type: 'streak' },
  { id: 'streak_30', name: 'Monthly Guardian', description: '30-day verification streak', icon: 'trophy', threshold: 30, type: 'streak' },
  { id: 'safe_finder', name: 'Threat Hunter', description: 'Found 5 high-risk items', icon: 'alert-triangle', threshold: 5, type: 'dangerous' },
  { id: 'email_expert', name: 'Phishing Expert', description: 'Verified 20 emails', icon: 'mail', threshold: 20, type: 'email' },
  // ── v1.0 additions ────────────────────────────────────────────────
  { id: 'comparison_analyst', name: 'Comparison Analyst', description: 'Ran 5 article comparisons', icon: 'git-compare', threshold: 5, type: 'comparison' },
  { id: 'misinformation_hunter', name: 'Misinformation Hunter', description: 'Detected 5 low-credibility articles', icon: 'search-x', threshold: 5, type: 'misinformation' },
  { id: 'phishing_investigator', name: 'Phishing Investigator', description: 'Ran 5 phishing lab sessions', icon: 'flask-conical', threshold: 5, type: 'phishingLab' },
  { id: 'threat_researcher', name: 'Threat Researcher', description: 'Completed 10 lab analyses', icon: 'microscope', threshold: 10, type: 'threatResearch' },
];

export const calculateStreak = (history) => {
  if (!history || history.length === 0) return 0;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const uniqueDays = new Set();
  history.forEach(item => {
    const date = new Date(item.timestamp);
    date.setHours(0, 0, 0, 0);
    uniqueDays.add(date.toISOString());
  });
  
  const sortedDays = Array.from(uniqueDays).sort((a, b) => new Date(b) - new Date(a));
  
  let streak = 0;
  let currentDate = new Date(today);
  
  for (let i = 0; i < sortedDays.length; i++) {
    const dayDate = new Date(sortedDays[i]);
    const diffTime = currentDate - dayDate;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 1) {
      streak++;
      currentDate = dayDate;
      currentDate.setDate(currentDate.getDate() - 1);
    } else {
      break;
    }
  }
  
  return streak;
};

export const getEarnedBadges = (stats) => {
  const earned = [];
  
  BADGES.forEach(badge => {
    if (badge.type === 'streak') {
      if (stats.streak >= badge.threshold) earned.push(badge);
    } else if (badge.type === 'dangerous') {
      if (stats.dangerousFound >= badge.threshold) earned.push(badge);
    } else if (badge.type === 'email') {
      if ((stats.emailsVerified || 0) >= badge.threshold) earned.push(badge);
    } else if (badge.type === 'comparison') {
      if ((stats.comparisonsRun || 0) >= badge.threshold) earned.push(badge);
    } else if (badge.type === 'misinformation') {
      if ((stats.misinformationFound || 0) >= badge.threshold) earned.push(badge);
    } else if (badge.type === 'phishingLab') {
      if ((stats.phishingLabSessions || 0) >= badge.threshold) earned.push(badge);
    } else if (badge.type === 'threatResearch') {
      if ((stats.threatResearchCount || 0) >= badge.threshold) earned.push(badge);
    } else {
      if (stats.totalVerifications >= badge.threshold) earned.push(badge);
    }
  });
  
  return earned;
};

export const getNextBadge = (stats) => {
  const unearned = BADGES.filter(badge => {
    if (badge.type === 'streak') return stats.streak < badge.threshold;
    if (badge.type === 'dangerous') return stats.dangerousFound < badge.threshold;
    if (badge.type === 'email') return (stats.emailsVerified || 0) < badge.threshold;
    if (badge.type === 'comparison') return (stats.comparisonsRun || 0) < badge.threshold;
    if (badge.type === 'misinformation') return (stats.misinformationFound || 0) < badge.threshold;
    if (badge.type === 'phishingLab') return (stats.phishingLabSessions || 0) < badge.threshold;
    if (badge.type === 'threatResearch') return (stats.threatResearchCount || 0) < badge.threshold;
    return stats.totalVerifications < badge.threshold;
  });
  
  return unearned[0] || null;
};
